// src/utils/blockchain.js
import { ethers } from 'ethers';

// --- ENV ---
const PGT_ADDRESS = process.env.REACT_APP_PGT_ADDRESS;
const EVENT_TICKET_ADDRESS = process.env.REACT_APP_EVENT_TICKET_ADDRESS;
const BASE_RPC_URL = process.env.REACT_APP_BASE_RPC_URL?.trim() || process.env.REACT_APP_BASE_RPC?.trim()
  || 'https://base-mainnet.g.alchemy.com/v2/abklby2rfDbW09WF-2pYU8Eul5wre5nq';

// --- ABI ---
const PGTTokenABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  // payGasFee больше не используется в варианте 2 — можно удалить
];

const EVENT_TICKET_ABI = [
  "function executeMintTicket(address user, uint256 ticketTypeId, uint256 amount, uint256 nonce, bytes signature) external",
  "function ticketTypes(uint256) view returns (uint256 price, uint256 maxSupply, uint256 saleStart, uint256 saleEnd, uint256 transferLockStart, uint256 transferLockEnd, string metadataURI, uint256[] perks)",
  "function totalSupplyByType(uint256) view returns (uint256)",
  "function nextSerialNumber(uint256) view returns (uint256)",
  "function balanceOf(address owner) view returns (uint256)",
  "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
];

// --- Helpers ---
// eslint-disable-next-line no-unused-vars
const getInjected = () =>
  typeof window !== 'undefined' && window.ethereum ? window.ethereum : null;

export const getBrowserProvider = () => {
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No injected wallet found');
  }
  return new ethers.BrowserProvider(window.ethereum);
};

export const getReadOnlyProvider = () => new ethers.JsonRpcProvider(BASE_RPC_URL);

// --- Contract Builders ---
export const buildReadOnly = (provider = getReadOnlyProvider()) => ({
  provider,
  pgtContract: new ethers.Contract(PGT_ADDRESS, PGTTokenABI, provider),
  eventTicket: new ethers.Contract(EVENT_TICKET_ADDRESS, EVENT_TICKET_ABI, provider),
});

export const withSigner = async (providerOrNull, contract) => {
  const provider = providerOrNull instanceof ethers.BrowserProvider
    ? providerOrNull
    : getBrowserProvider();
  const signer = await provider.getSigner();
  return contract.connect(signer);
};

// --- Main Loader (backward compatible) ---
export const loadContracts = async (provider, { requestSigner = false } = {}) => {
  const base = buildReadOnly(provider ?? getReadOnlyProvider());
  if (!requestSigner) return base;

  const bp = provider instanceof ethers.BrowserProvider ? provider : getBrowserProvider();
  const pgtWrite = await withSigner(bp, base.pgtContract);
  const eventTicketWrite = await withSigner(bp, base.eventTicket);
  return {
    provider: bp,
    pgtContract: pgtWrite,
    eventTicket: eventTicketWrite,
  };
};

// --- Read Utils ---
export const getPGTBalance = async (address, provider) => {
  const ro = buildReadOnly(provider);
  const bal = await ro.pgtContract.balanceOf(address);
  return bal; // возвращаем BigInt или string — не преобразуем в число
};

export const checkPGTAllowance = async (owner, spender, provider) => {
  const ro = buildReadOnly(provider);
  return await ro.pgtContract.allowance(owner, spender);
};

// --- Write Utils ---
export const approvePGT = async (spender, amount, provider) => {
  const ro = buildReadOnly(provider);
  const writable = await withSigner(provider, ro.pgtContract);
  const tx = await writable.approve(spender, amount);
  return tx.wait();
};

// --- EIP-712 Signing for Gasless Mint ---
export const signMintTicketRequest = async (signer, user, ticketTypeId, amount, nonce, resolvedChainId) => {
  const domain = {
    name: 'EventTicketWithEvents',
    version: '1',
    chainId: Number(resolvedChainId || process.env.REACT_APP_CHAIN_ID || 84532),
    verifyingContract: EVENT_TICKET_ADDRESS,
  };

  const types = {
    MintTicket: [
      { name: 'user', type: 'address' },
      { name: 'ticketTypeId', type: 'uint256' },
      { name: 'amount', type: 'uint256' },
      { name: 'nonce', type: 'uint256' },
    ],
  };

  // Передаём как обычные числа — ethers сам обработает
  const value = {
    user,
    ticketTypeId: Number(ticketTypeId),
    amount: Number(amount),
    nonce: Number(nonce),
  };

  return await signer._signTypedData(domain, types, value);
};

// --- Utility: Format PGT (2 decimals) ---
export const formatPGT = (rawAmount) => {
  if (rawAmount == null || rawAmount === '') return '0.00';
  let num;
  if (typeof rawAmount === 'bigint') {
    num = Number(rawAmount);
  } else if (typeof rawAmount === 'object' && rawAmount.toString) {
    num = Number(rawAmount.toString());
  } else {
    num = Number(rawAmount);
  }
  if (!Number.isFinite(num) || num < 0) return '0.00';
  return (num / 100).toFixed(2);
};

// --- Perk Utils ---
export const getPerkInfo = (perkId) => {
  const perks = {
    1: { name: "Free Drink", description: "One complimentary beverage" },
    2: { name: "VIP Access", description: "Exclusive VIP area access" },
    3: { name: "Merch Discount", description: "20% off merchandise" }
  };
  return perks[perkId] || { name: `Perk ${perkId}`, description: "Special event perk" };
};